import { useState, useEffect, useRef } from 'react';
import type { BadgeCounts } from '@/api/badges';
import { useAuth } from './useAuth';
import { UserRole } from '@/lib/schema';

interface UseBadgesReturn {
  badges: BadgeCounts;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook for managing real-time badge counts
 * Automatically subscribes to Firestore collections and provides real-time updates
 */
export function useBadges(): UseBadgesReturn {
  const { currentUser, userData } = useAuth();
  const [badges, setBadges] = useState<BadgeCounts>({
    referrals: 0,
    clientReferrals: 0,
    rewards: 0,
    cvMissing: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Manual refetch function
  const refetch = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      // Dynamic import to keep badges out of main bundle
      const { getAdminBadgeCounts } = await import('@/api/badges');
      const counts = await getAdminBadgeCounts();
      setBadges(counts);
    } catch (err) {
      console.error('Error refetching badges:', err);
      setError('Failed to fetch badge counts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Only set up listeners for authenticated admin users
    if (!currentUser || !userData) {
      setLoading(false);
      return;
    }

    // Check if user has admin privileges
    const isAdmin = userData.role === UserRole.SUPER_ADMIN || userData.role === UserRole.RECRUITER;
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Dynamic import to keep badges out of main bundle
    import('@/api/badges').then(({ subscribeToAdminBadges }) => {
      const unsubscribe = subscribeToAdminBadges((newBadges) => {
        setBadges(newBadges);
        setLoading(false);
        setError(null);
      });

      unsubscribeRef.current = unsubscribe;
    }).catch((err) => {
      console.error('Error loading badges:', err);
      setError('Failed to load badges');
      setLoading(false);
    });

    // Cleanup on unmount or dependency change
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [currentUser, userData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return {
    badges,
    loading,
    error,
    refetch
  };
}

/**
 * Hook for specific badge type with custom filters
 */
export function useSpecificBadge(
  collectionName: string,
  filters: Record<string, any> = {}
): {
  count: number;
  loading: boolean;
  error: string | null;
} {
  const { currentUser } = useAuth();
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // Dynamic import to avoid circular dependencies
    import('@/api/badges').then(({ subscribeToCollectionCount }) => {
      const unsubscribe = subscribeToCollectionCount(
        collectionName,
        (newCount) => {
          setCount(newCount);
          setLoading(false);
          setError(null);
        },
        filters
      );

      unsubscribeRef.current = unsubscribe;
    }).catch((err) => {
      console.error('Error setting up badge listener:', err);
      setError('Failed to set up badge listener');
      setLoading(false);
    });

    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [currentUser, collectionName, JSON.stringify(filters)]);

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
      }
    };
  }, []);

  return {
    count,
    loading,
    error
  };
}
